<template lang="pug">
.section.section-tabs(v-if="block" :style="{ '--columns': block?.columns || 2 }")
  .section-tabs__content.section__content
    h2.section__title(v-html="block.title")
    |
    p.section__description(v-html="block.description")
  |
  .section-tabs__controls
    .help
      span.help__title(v-if="block.helpTitle") {{ block.helpTitle }}
      |
      .tooltip.tooltip-primary(
        v-if="block.helpInfo"
        :data-tip="block.helpInfo"
      )
        IconInfoCircle
      |
      span.help__label(v-if="block.helpLabel" v-html="block.helpLabel")
    |
    .controls-container
      .controls(
        v-if="block.tabs"
      )
        .controls__item(
          v-for="(tab, index) in block.tabs"
          :key="index"
          :class="{ active: selectedTabIndex === index }"
          @click="selectedTabIndex = index"
        )
          .controls__item__title {{ tab.title }}
          |
          .controls__item__label {{ tab.label }}
  |
  .section-tabs__blocks(v-if="block.tabs[selectedTabIndex].pricingOptions")
    PricingBlock(
      v-for="(pricingItem, index) in block.tabs[selectedTabIndex].pricingOptions"
      :key="selectedTabIndex"
      :pricing-item="pricingItem"
    )
</template>

<script setup lang="ts">
import { IconCheck, IconInfoCircle } from '@tabler/icons-vue';
import type { TBlock } from "@/models/block";

interface IProps {
  block?: TBlock | null;
}

const props = withDefaults(defineProps<IProps>(), {
  block: null,
});

const selectedTabIndex = ref(0);
</script>

<style lang="sass" scoped>
.section-tabs
  padding-top: 100px
  padding-bottom: 100px

  &__content
    grid-column-start: 1
    grid-column-end: 13

  &__blocks
    grid-column-start: 1
    grid-column-end: 13
    display: grid
    grid-template-columns: repeat(var(--columns, 3), 1fr)
    gap: 24px
    margin-left: 0
    margin-right: 0
  
  &__controls
    grid-column-start: 1
    grid-column-end: 13
    display: grid
    grid-template-columns: repeat(12, 1fr)
    gap: 0 21px
    margin-bottom: 24px
    margin-top: 80px

    .help
      grid-column-start: 1
      grid-column-end: 4
      display: flex
      justify-content: flex-start
      align-items: center
      gap: 4px

      &__title
        color: var(--deep-teal-500, #698E82)
        font-size: 14px
        font-style: normal
        font-weight: 700
        line-height: 20px
        white-space: nowrap

      &__label
        color: var(--deep-teal-500, #698E82)
        font-size: 14px
        font-style: normal
        font-weight: 400
        line-height: 20px
        white-space: nowrap
    
    .controls-container
      grid-column-start: 5
      grid-column-end: 9
      display: flex
      justify-content: center

    .controls
      display: flex
      padding: var(--Spacing-4XS, 6px)
      align-items: flex-start
      border-radius: 999px
      border: 1px solid var(--darkilimegreen-100, #D6DFC5)
      background: var(--darkilimegreen-50, #EBF0E1)
      width: fit-content

      &__item
        border-radius: 999px
        display: flex
        padding: 6.5px 12px
        justify-content: center
        align-items: center
        gap: 4px
        cursor: pointer
        transition: 0.2s

        &.active
          background: var(--tealgreen-600, #10715A)
          pointer-events: none

          .controls__item__title
            color: var(--deep-teal-25, #E9F5F0)

          .controls__item__label
            color: var(--deep-teal-25, #E9F5F0)

        &__title
          color: var(--deep-teal-700, #3F5A51)
          text-align: center
          font-size: 14px
          font-style: normal
          font-weight: 600
          line-height: 20px
          transition: 0.2s

        &__label
          color: var(--deep-teal-600, #537469)
          text-align: center
          font-size: 14px
          font-style: normal
          font-weight: 400
          line-height: 20px
          transition: 0.2s

@media (max-width: 1275px)
  .section-tabs
    padding-top: 60px
    padding-bottom: 60px

    &__blocks
      grid-template-columns: 1fr

    .section__title
      font-size: 32px
      line-height: 37px

    &__controls
      margin-top: 40px

      .help
        grid-column-start: 1
        grid-column-end: 13
        margin-bottom: 20px
        justify-content: center

      .controls-container
        grid-column-start: 1
        grid-column-end: 13
</style>